import { useState } from "react";
import { GitMerge, X, ArrowRight } from "lucide-react";

/**
 * Merge one bank account into another — for when the same real account was
 * imported under two different names. All transactions move onto the target,
 * duplicates are dropped, and the source account is removed.
 */
export function MergeAccountsDialog({ accounts = [], onMerge, onClose }) {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [merging, setMerging] = useState(false);

  const srcAcc = accounts.find((a) => a.id === source);
  const tgtAcc = accounts.find((a) => a.id === target);
  const canMerge = source && target && source !== target;

  async function submit() {
    if (!canMerge) return;
    setMerging(true);
    await onMerge(source, target);
    setMerging(false);
  }

  const selectCls = "input-field w-full";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="merge-accounts-dialog">
      <div className="bg-white border border-[#E5E3DB] w-full max-w-md shadow-xl">
        <div className="flex items-start justify-between p-5 border-b border-[#E5E3DB]">
          <div className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#C84B31]" />
            <div>
              <div className="font-headings text-lg font-bold text-[#1A1A18]">Merge accounts</div>
              <div className="text-xs text-[#68665E] mt-0.5">Combine the same account imported under two names.</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="merge-close"><X className="w-4 h-4 text-[#68665E]" /></button>
        </div>

        <div className="p-5 space-y-4">
          {accounts.length < 2 ? (
            <p className="text-sm text-[#68665E]" data-testid="merge-need-two">
              You need at least two saved accounts to merge. Upload another statement or set a bank on an existing one first.
            </p>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-1.5">Merge this account</label>
                <select value={source} onChange={(e) => setSource(e.target.value)} className={selectCls} data-testid="merge-source">
                  <option value="">Select account…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-center text-[#A09E96]">
                <ArrowRight className="w-4 h-4 rotate-90" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-1.5">Into this account (kept)</label>
                <select value={target} onChange={(e) => setTarget(e.target.value)} className={selectCls} data-testid="merge-target">
                  <option value="">Select account…</option>
                  {accounts.filter((a) => a.id !== source).map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              {canMerge && (
                <p className="text-xs text-[#8A2526] bg-[#FBEDED] border border-[#8A2526]/30 p-2.5 rounded" data-testid="merge-warning">
                  All transactions from <span className="font-semibold">{srcAcc?.label}</span> will move into{" "}
                  <span className="font-semibold">{tgtAcc?.label}</span>, duplicates will be removed, and{" "}
                  <span className="font-semibold">{srcAcc?.label}</span> will be deleted. This can't be undone.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-[#E5E3DB]">
          <button onClick={onClose} className="btn-secondary" data-testid="merge-cancel">Cancel</button>
          <button onClick={submit} disabled={!canMerge || merging} className="btn-primary disabled:opacity-50" data-testid="merge-confirm">
            {merging ? "Merging…" : "Merge accounts"}
          </button>
        </div>
      </div>
    </div>
  );
}
